
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Folder, Trash2, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/auth";
import { useProjects } from "@/components/Auth/Projects/useProjects";
import { toast } from "sonner";

interface ProjectSelectorProps {
  projectId: string | null;
  onProjectChange: (projectId: string) => void;
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({ projectId, onProjectChange }) => {
  const { user } = useAuth();
  const { projects, sharedProjects, isLoading, refreshProjects, handleDeleteProject } = useProjects();
  const [isOpen, setIsOpen] = useState(false);
  
  // Find the current project in the list
  const currentProject = projects.find(p => p.id === projectId) || 
                        sharedProjects.find(p => p.id === projectId);
  
  // Effect to refresh projects when the component mounts and log current state
  useEffect(() => {
    console.log("ProjectSelector mounted, current state:", {
      projectId,
      projectsCount: projects.length,
      sharedProjectsCount: sharedProjects.length,
      isLoading,
      user: user?.id
    });
    
    if (projects.length === 0 && !isLoading && user) {
      console.log("No projects found, triggering refresh...");
      refreshProjects();
    }
  }, [projectId, refreshProjects, projects.length, sharedProjects.length, isLoading, user]);
  
  const handleProjectSelect = (newProjectId: string) => {
    if (newProjectId === projectId) {
      setIsOpen(false);
      return;
    }
    
    console.log("Project selected in ProjectSelector:", newProjectId);
    onProjectChange(newProjectId);
    setIsOpen(false);
    toast.success("Projekt valt");
  };

  const handleDeleteOldProject = async (projectIdToDelete: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (confirm("Är du säker på att du vill ta bort detta projekt? Detta kan inte ångras.")) {
      const success = await handleDeleteProject(projectIdToDelete);
      if (success) {
        toast.success("Projekt borttaget");
        // If we deleted the current project, clear the selection
        if (projectIdToDelete === projectId) {
          onProjectChange("");
        }
        refreshProjects();
      }
    }
  };

  const handleManualRefresh = () => {
    console.log("Manual refresh triggered from ProjectSelector");
    refreshProjects();
    toast.info("Uppdaterar projektlista...");
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="min-w-[180px] max-w-[280px] justify-between glassmorphism bg-white/10 hover:bg-white/20 border-white/20 text-marine-900 shadow-sm backdrop-blur-xl"
          disabled={isLoading}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Folder className="h-4 w-4 text-terracotta flex-shrink-0" />
            {isLoading ? (
              <span className="truncate text-marine-900">Laddar...</span>
            ) : (
              <span className="truncate text-marine-900">
                {currentProject?.name || "Välj projekt"}
              </span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 text-marine-700 flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="start" className="w-[360px] max-h-[400px] overflow-y-auto glassmorphism bg-marine-800/95 border-white/20 shadow-lg backdrop-blur-2xl">
        {/* Refresh button at the top */}
        <DropdownMenuItem 
          className="flex items-center justify-center border-b border-white/20 py-2 cursor-pointer hover:bg-white/10 text-on-hero"
          onClick={handleManualRefresh}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          <span className="text-sm">Uppdatera projektlista</span>
        </DropdownMenuItem>
        
        {/* My Projects Section */}
        {projects.length > 0 && (
          <>
            <div className="px-3 py-2 text-xs font-medium text-on-hero/80 bg-white/5 border-b border-white/20">
              Mina projekt ({projects.length})
            </div>
            {projects.map((project) => (
              <DropdownMenuItem
                key={project.id}
                className="flex items-center justify-between cursor-pointer hover:bg-white/10 group px-3 py-2 text-on-hero"
                onClick={() => handleProjectSelect(project.id)}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Folder className="h-4 w-4 text-terracotta flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className="truncate text-sm font-medium text-on-hero">{project.name}</span>
                    {project.description && (
                      <div className="text-xs text-on-hero/60 truncate">{project.description}</div>
                    )}
                  </div>
                  {project.id === projectId && (
                    <span className="text-xs bg-terracotta/20 text-terracotta px-2 py-0.5 rounded flex-shrink-0">
                      Aktiv
                    </span>
                  )}
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:bg-red-600/20 hover:text-red-400 flex-shrink-0"
                  onClick={(e) => handleDeleteOldProject(project.id, e)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </DropdownMenuItem>
            ))}
          </>
        )}
        
        {/* Shared Projects Section */}
        {sharedProjects.length > 0 && (
          <>
            <div className="px-3 py-2 text-xs font-medium text-champagne/80 bg-champagne/10 border-b border-t border-white/20">
              Delade med dig ({sharedProjects.length})
            </div>
            {sharedProjects.map((project) => (
              <DropdownMenuItem
                key={project.id}
                className="flex items-center justify-between cursor-pointer hover:bg-white/10 px-3 py-2 text-on-hero"
                onClick={() => handleProjectSelect(project.id)}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Folder className="h-4 w-4 text-champagne flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className="truncate text-sm font-medium text-on-hero">{project.name}</span>
                    {project.description && (
                      <div className="text-xs text-on-hero/60 truncate">{project.description}</div>
                    )}
                  </div>
                  {project.id === projectId && (
                    <span className="text-xs bg-champagne/20 text-champagne px-2 py-0.5 rounded flex-shrink-0">
                      Aktiv
                    </span>
                  )}
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}
        
        {/* Empty state or loading */}
        {projects.length === 0 && sharedProjects.length === 0 && (
          <div className="px-3 py-4 text-center text-sm text-on-hero/60">
            {isLoading ? (
              <div className="flex items-center justify-center">
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Laddar projekt...
              </div>
            ) : (
              <div>
                <div>Inga projekt tillgängliga</div>
                <div className="text-xs text-on-hero/40 mt-1">
                  Klicka på "Uppdatera projektlista" ovan för att försöka igen
                </div>
              </div>
            )}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProjectSelector;
